import {
  debounce,
  ElementController,
  getData,
  getSVGIcon,
  insertBefore,
  modifierKeyWatcher,
  parseHTML,
  selectAll,
  selectSingle,
  selectChild,
  TOGGLE_EVENT,
} from 'js-common';
import { isButtonActivationKey } from './utils';

const DROPDOWN_TITLE_ITEM_CLASS = 'title-item';
const SEARCH_ITEM_SELECTOR = '.header-search-item';
const TOP_LEVEL_PARENT_DATA_ATTR = 'top-level-parent';

/**
 * Main and secondary navigation dropdown menus.
 *
 * The main purpose with this is to make the dropdown menus work for all people,
 * not just those with mice or other precision pointers that have a hover state.
 *
 * - For touch screens, the dropdown parent links behave like buttons that open
 *   the dropdown on click instead of going to the link URL.
 * - For keyboard users, the open state is set on the dropdown parent li on
 *   focus and kept when focus is inside the dropdown.
 *
 * In both cases, the aria-expanded attribute is set on the parent li and the
 * CSS must treat `[aria-expanded='true']` just like :hover.
 *
 * On smaller screens the dropdowns are toggled on click without any auto
 * expansion for keyboard focus.
 *
 * @extends {ElementController}
 */
export default class Menu extends ElementController {
  static id = 'Menu';

  /**
   * DOM loaded, initialize elements and bind events.
   */
  init() {
    const menuName = getData(this.root, 'menu-name');

    this.menuId = this.root.id;
    this.dropdownCount = 0;
    this.isPrimaryMenu = menuName === 'primary';

    // The primary menu will switch to a toggle version on smaller screens.
    if (this.isPrimaryMenu) {
      this.menuToggle = selectSingle('[data-menu-toggle]');
      this.menuToggleStyle = window.getComputedStyle(this.menuToggle);
      this.menuList = selectChild(this.root, 'ul');

      this.on(this.root, 'click', this.handleRootClick);
      this.checkMenuToggleState();
      this.on(window, 'resize', debounce(this.handleResize, 200));
    }

    // Get elements.
    this.topLevelParentLinks = selectAll(
      this.root,
      '[data-level="1"] > .has-children > a',
    );

    // Make adjustments for functionality and accessibility that are only
    // relevant with JS available.
    this.dropdownify();

    // Search form toggle.
    const searchItem = selectSingle(this.root, SEARCH_ITEM_SELECTOR);
    if (searchItem) {
      this.searchInput = selectSingle(searchItem, 'input[type="search"]');
      this.searchToggle = selectChild(searchItem, '[data-controller="Toggle"]');
      this.on(this.searchToggle, TOGGLE_EVENT.toggle, this.handleSearchToggle);
      this.on(this.searchInput, 'keydown', this.handleSearchInputKeydown);
    }

    // Bind events for the dropdown parent links.
    this.on(
      this.root,
      'click',
      '[role="button"]',
      this.handleButtonizedLinkClick,
    );
    this.on(
      this.root,
      'keydown',
      '[role="button"]',
      this.handleButtonizedLinkKeydown,
    );

    this.on(document.body, 'click', this.handleBodyClick);

    if (this.isPrimaryMenu) {
      this.openActiveToggledMenuItem();
    }
  }

  /**
   * Check if the menu is currently in its toggled state.
   */
  checkMenuToggleState = () => {
    this.isToggledMenu = this.menuToggleStyle.display !== 'none';
  };

  /**
   * Make adjustments to menu items with children.
   *
   * - Adds the top level link as the first item in the dropdown.
   * - Makes links behave like buttons.
   * - Binds events to make keyboard focus work.
   */
  dropdownify() {
    this.topLevelParentLinks.forEach((a) => {
      /* eslint-disable no-param-reassign */
      this.dropdownCount += 1;
      const dropdownId = `${this.menuId}-dropdown-${this.dropdownCount}`;

      // Add the main link as the first sub menu item.
      const subMenu = selectChild(a.parentNode, '.sub-menu');
      // Do a shallow clone and set the text to get rid of the .text span
      const linkClone = a.cloneNode();
      linkClone.textContent = a.textContent.trim();
      insertBefore(
        subMenu.firstChild,
        parseHTML(
          `<li class="${DROPDOWN_TITLE_ITEM_CLASS}">${linkClone.outerHTML}</li>`,
        ),
      );

      // Check if the sub menu is only one level deep
      const subMenuLi = selectAll(subMenu, 'li');
      if (!subMenuLi.some((li) => li.classList.contains('has-children'))) {
        subMenu.classList.add('sub-menu--single-level');
      }

      // Make the link behave like a button, bind it to the dropdown and add
      // a chevron icon.
      a.setAttribute('role', 'button');
      a.setAttribute('aria-controls', dropdownId);
      subMenu.id = dropdownId;
      a.setAttribute('aria-expanded', 'false');
      a.append(parseHTML(getSVGIcon('chevron-down')));

      // Close when leaving the last link
      a.parentNode.setAttribute(`data-${TOP_LEVEL_PARENT_DATA_ATTR}`, '');
      const lastSubMenuLink = selectAll(subMenu, 'a').pop();
      this.on(lastSubMenuLink, 'blur', this.handleLastDropdownLinkBlur);
    });
  }

  /**
   * Set a dropdown's opened state.
   *
   * @param {Element} target - Should be a dropdown menu's parent li.
   */
  setOpenedState(target) {
    selectChild(target, 'a').setAttribute('aria-expanded', 'true');
  }

  /**
   * Remove a dropdown's opened state.
   *
   * @param {Element} target - Should be a dropdown menu's parent li.
   */
  removeOpenedState(target) {
    selectChild(target, 'a').setAttribute('aria-expanded', 'false');
  }

  /**
   * Remove opened state from all dropdowns.
   *
   * @param {...Element} excludeLi - Items to exclude, should be a dropdown
   *   menu's parent li.
   */
  removeAllOpenedStates(...excludeLi) {
    const activeLinks = this.topLevelParentLinks.filter(
      (link) => link.getAttribute('aria-expanded') === 'true',
    );
    if (activeLinks.length) {
      activeLinks
        .filter((link) => !excludeLi.includes(link.parentNode))
        .forEach((link) => {
          this.removeOpenedState(link.parentNode);
        });
    }
  }

  /**
   * Toggle a dropdown's opened state.
   *
   * @param {Element} target - Should be a dropdown menu's parent li.
   */
  toggleOpenState(target) {
    const link = selectChild(target, 'a');
    link.setAttribute(
      'aria-expanded',
      String(link.getAttribute('aria-expanded') === 'false'),
    );
  }

  /**
   * Check if a menu item is active.
   *
   * @param {Element} target - Should be a dropdown menu's parent li.
   */
  itemIsActive(target) {
    return (
      target.classList.contains('current-item') ||
      target.classList.contains('current-item-ancestor')
    );
  }

  /**
   * Set the current item to open if the menu is in its toggled state.
   */
  openActiveToggledMenuItem() {
    if (this.isToggledMenu) {
      this.topLevelParentLinks.forEach((link) => {
        if (this.itemIsActive(link.parentNode)) {
          this.setOpenedState(link.parentNode);
        }
      });
    }
  }

  /**
   * Check toggled menu state on resize.
   */
  handleResize = () => {
    const isToggledBeforeResize = this.isToggledMenu;
    this.checkMenuToggleState();
    // Menu items can be opened by 'toggle clicking' when the toggled menu is
    // active. If a resize then restores the full menu, several dropdowns can
    // be active which looks pretty wonky.
    if (isToggledBeforeResize && !this.isToggledMenu) {
      this.topLevelParentLinks.forEach((link) => {
        this.removeOpenedState(link.parentNode);
      });
    }
    this.openActiveToggledMenuItem();
  };

  /**
   * Remove opened state when leaving the last link in a dropdown.
   *
   * @param {object} e - Blur event.
   */
  handleLastDropdownLinkBlur = (e) => {
    if (!this.isToggledMenu) {
      const topLevelItem = e.currentTarget.closest(
        `[data-${TOP_LEVEL_PARENT_DATA_ATTR}]`,
      );
      // Don't close if tabbing from the last item back to a previous item
      if (e.relatedTarget && topLevelItem.contains(e.relatedTarget)) {
        return;
      }
      this.removeOpenedState(topLevelItem);
    }
  };

  /**
   * Prevent dropdown parent links from opening.
   *
   * @param {object} e - Click event.
   */
  handleButtonizedLinkClick = (e) => {
    if (!modifierKeyWatcher.isHoldingNewTabKey()) {
      e.preventDefault();
      this.toggleOpenState(e.currentTarget.parentNode);
    }
  };

  /**
   * Prevent dropdown link parents from activating unless it's to open them
   * in a new tab.
   *
   * @param {object} e - Keydown event.
   */
  handleButtonizedLinkKeydown = (e) => {
    // Ctrl/Cmd/Alt + Enter is for opening in a new tab just like for click.
    if (
      isButtonActivationKey(e.key) &&
      !modifierKeyWatcher.isHoldingNewTabKey()
    ) {
      e.preventDefault();
      // Trigger any click handlers
      e.currentTarget.click();
    }
  };

  /**
   * Close the toggled menu when clicking on the 'background overlay', which is
   * actually just the menu background.
   *
   * @param {object} e - Click event.
   */
  handleRootClick = (e) => {
    if (
      this.isPrimaryMenu &&
      this.isToggledMenu &&
      // Not clicking on something inside the list
      !this.menuList.contains(e.target)
    ) {
      this.menuToggle.click();
    }
  };

  /**
   * Focus the search input when opening its dialog.
   *
   * @param {object} e - Toggle event.
   * @param {boolean} isOpen - If the toggle is open.
   */
  handleSearchToggle = (e, isOpen) => {
    if (isOpen) {
      this.searchInput.focus();
    }
  };

  /**
   * Focus the search input when opening its dialog.
   *
   * @param {object} e - Keydown event.
   */
  handleSearchInputKeydown = (e) => {
    if (e.key === 'Escape') {
      this.searchToggle.click();
      // Try to let the button go back to its collapsed state before setting
      // focus back on it.
      setTimeout(() => {
        this.searchToggle.focus();
      });
    }
  };

  /**
   * Close open dropdowns when clicking outside them.
   *
   * @param {object} e - Keydown event.
   */
  handleBodyClick = (e) => {
    if (!this.isToggledMenu) {
      const parentItem = e.target.closest(
        `[data-${TOP_LEVEL_PARENT_DATA_ATTR}]`,
      );
      // Exclude any current one
      this.removeAllOpenedStates(parentItem);
    }
  };
}
